// client/src/utils/api/properties.js

import { api, classifyApiError, handleRequestError } from './config';
import { getLogger } from '../logger';
import { createIdempotencyKey } from '../idempotencyKey';

const log = getLogger('propertiesApi');

const PROPERTY_FETCH_ERROR_CODE_BY_KIND = {
  canceled: "PROPERTY_FETCH_CANCELED",
  http: "PROPERTY_FETCH_HTTP_ERROR",
  network: "PROPERTY_FETCH_NETWORK_ERROR",
  unknown: "PROPERTY_FETCH_UNKNOWN_ERROR",
};

export class PropertyFetchError extends Error {
  constructor({ propertyId, kind, status, originalError }) {
    super(`Failed to fetch property ${propertyId}`);
    this.name = "PropertyFetchError";
    this.propertyId = propertyId;
    this.kind = PROPERTY_FETCH_ERROR_CODE_BY_KIND[kind] ? kind : "unknown";
    this.status = status ?? null;
    this.code = PROPERTY_FETCH_ERROR_CODE_BY_KIND[this.kind];
    this.isCanceled = this.kind === "canceled";
    this.isRetryable =
      this.kind === "network" || (this.kind === "http" && Number(this.status) >= 500);
    this.cause = originalError;
    this.originalError = originalError;
  }
}

const toPropertyFetchError = (propertyId, error) => {
  const { kind, status } = classifyApiError(error);
  return new PropertyFetchError({
    propertyId,
    kind,
    status,
    originalError: error,
  });
};


/**
 * Get all properties (full data)
 * @returns {Promise<Array>} List of all properties with all fields
 */
export const getAllProperties = async () => {
  try {
    log.info(
      "[properties:getAllProperties] > [Request]: GET /property/all > [Response]: pending"
    );
    const response = await api.get('/property/all');
    log.info(
      `[properties:getAllProperties] > [Request]: GET /property/all > [Response]: received=${Array.isArray(response.data) ? response.data.length : "unknown"}`
    );
    return response.data;
  } catch (error) {
    log.error(
      `[properties:getAllProperties] > [Request]: GET /property/all > [Response]: error=${error?.message || "unknown"}`
    );
    handleRequestError(error, "Failed to fetch properties");
  }
};

/**
 * Get all properties for the admin table, bypassing status visibility filters.
 * Only honored by the server for admin-authorized requests.
 * @returns {Promise<Array>} List of all properties with all fields
 */
export const getAllPropertiesForAdminTable = async () => {
  try {
    log.info(
      "[properties:getAllPropertiesForAdminTable] > [Request]: GET /property/all?includeAllStatuses=true > [Response]: pending"
    );
    const response = await api.get("/property/all", {
      params: { includeAllStatuses: true },
    });
    log.info(
      `[properties:getAllPropertiesForAdminTable] > [Request]: GET /property/all?includeAllStatuses=true > [Response]: received=${Array.isArray(response.data) ? response.data.length : "unknown"}`
    );
    return response.data;
  } catch (error) {
    log.error(
      `[properties:getAllPropertiesForAdminTable] > [Request]: GET /property/all?includeAllStatuses=true > [Response]: error=${error?.message || "unknown"}`
    );
    handleRequestError(error, "Failed to fetch admin properties");
  }
};

/**
 * Get all properties summary (optimized for listing pages)
 * Returns only essential fields needed for PropertyCard/grid views
 * ~60-70% smaller payload than getAllProperties
 * @returns {Promise<Array>} List of properties with essential fields only
 */
export const getAllPropertiesSummary = async () => {
  try {
    log.info(
      "[properties:getAllPropertiesSummary] > [Request]: GET /property/summary > [Response]: pending"
    );
    const response = await api.get('/property/summary');
    log.info(
      `[properties:getAllPropertiesSummary] > [Request]: GET /property/summary > [Response]: received=${Array.isArray(response.data) ? response.data.length : "unknown"}`
    );
    return response.data;
  } catch (error) {
    log.error(
      `[properties:getAllPropertiesSummary] > [Request]: GET /property/summary > [Response]: error=${error?.message || "unknown"}`
    );
    handleRequestError(error, "Failed to fetch properties summary");
  }
};

/**
 * Get paginated properties (optimized with field selection)
 * Supports infinite scroll with page-based loading
 * @param {number} page - Page number (default: 1)
 * @param {number} limit - Items per page (default: 20)
 * @returns {Promise<Object>} Object with data and pagination metadata
 */
export const getAllPropertiesPaginated = async (page = 1, limit = 20) => {
  try {
    log.info(
      `[properties:getAllPropertiesPaginated] > [Request]: GET /property/paginated?page=${page}&limit=${limit} > [Response]: pending`
    );
    const response = await api.get('/property/paginated', {
      params: { page, limit }
    });
    log.info(
      `[properties:getAllPropertiesPaginated] > [Request]: GET /property/paginated?page=${page}&limit=${limit} > [Response]: received=${response.data?.data?.length || 0} items, page ${response.data?.pagination?.page || 'unknown'}`
    );
    return response.data;
  } catch (error) {
    log.error(
      `[properties:getAllPropertiesPaginated] > [Request]: GET /property/paginated?page=${page}&limit=${limit} > [Response]: error=${error?.message || "unknown"}`
    );
    handleRequestError(error, "Failed to fetch paginated properties");
  }
};

/**
 * Get a single property by ID
 * @param {string} id - Property ID
 * @returns {Promise<Object>} Property data
 */
export const getProperty = async (id, { includeAllStatuses = false } = {}) => {
  try {
    const response = await api.get(`/property/${id}`, {
      params: includeAllStatuses ? { includeAllStatuses: true } : undefined,
    });
    const property = response.data;
    if (property && !property.id && property._id) {
      return { ...property, id: property._id };
    }
    return property;
  } catch (error) {
    try {
      handleRequestError(error, "Failed to fetch property");
    } catch (normalizedError) {
      const propertyError = toPropertyFetchError(id, normalizedError);
      log.error(
        `[properties:getProperty] > [Request]: GET /property/${id} > [Response]: typed_error=${propertyError.code} > [Comment]: kind=${propertyError.kind},status=${propertyError.status ?? "none"}`
      );
      throw propertyError;
    }
  }
};

/**
 * Create a new property
 * @param {Object} propertyData - Property data
 * @returns {Promise<Object>} Created property
 */
export const createProperty = async (propertyData) => {
  try {
    const response = await api.post('/property/create', propertyData);
    return response.data;
  } catch (error) {
    handleRequestError(error, "Failed to create property");
  }
};

/**
 * Create property with files (images, documents)
 * @param {FormData} formData - Form data with property info and files
 * @returns {Promise<Object>} Created property
 */
export const createPropertyWithFiles = async (formData) => {
  try {
    const idempotencyKey = createIdempotencyKey("property-create", formData);
    const response = await api.post('/property/createWithFile', formData, {
      headers: {
        "Content-Type": "multipart/form-data",
        "Idempotency-Key": idempotencyKey,
      },
    });
    return response.data;
  } catch (error) {
    handleRequestError(error, "Failed to create property with files");
  }
};

/**
 * Create property with tracts (parent + child tract properties)
 * @param {FormData} formData - Property data including enableTracts and tractCount
 * @returns {Promise} - Created parent property and tracts
 */
export const createPropertyWithTracts = async (formData) => {
  try {
    const idempotencyKey = createIdempotencyKey("property-create-tracts", formData);
    const response = await api.post('/property/with-tracts', formData, {
      headers: {
        "Content-Type": "multipart/form-data",
        "Idempotency-Key": idempotencyKey,
      },
    });
    return response.data;
  } catch (error) {
    handleRequestError(error, "Failed to create property with tracts");
  }
};

/**
 * Get all tracts for a parent property
 * @param {string} parentId - Parent property ID
 * @returns {Promise} - Array of tract properties
 */
export const getTractsForProperty = async (parentId) => {
  try {
    const response = await api.get(`/property/${parentId}/tracts`);
    return response.data;
  } catch (error) {
    handleRequestError(error, "Failed to fetch property tracts");
  }
};

/**
 * Reorder a parent property's child tracts
 * @param {string} parentId - Parent property ID
 * @param {string[]} orderedIds - Child tract IDs in the desired display order
 * @returns {Promise} - Updated tract list in the new order
 */
export const reorderPropertyTracts = async (parentId, orderedIds) => {
  try {
    const response = await api.patch(`/property/${parentId}/tracts/reorder`, {
      order: orderedIds,
    });
    return response.data;
  } catch (error) {
    handleRequestError(error, "Failed to reorder property tracts");
  }
};

/**
 * Preview parent changes propagation to selected child tracts
 * @param {string} id - Parent property ID
 * @param {Object} draftData - Parent draft update payload
 * @returns {Promise<Object>} Propagation preview
 */
export const previewParentPropertyPropagation = async (id, draftData) => {
  try {
    const response = await api.post(`/property/${id}/propagation-preview`, draftData);
    return response.data;
  } catch (error) {
    handleRequestError(error, "Failed to preview tract propagation");
  }
};

export const getNewPropertyId = async () => {
  try {
    const response = await api.get('/property/new-id');
    return response.data;
  } catch (error) {
    handleRequestError(error, "Failed to create property ID");
  }
};

export const uploadPropertyImages = async (propertyId, files) => {
  try {
    const formData = new FormData();
    files.forEach((file) => formData.append("images", file));
    const response = await api.post(`/property/${propertyId}/images`, formData, {
      headers: { "Content-Type": "multipart/form-data" },
    });
    return response.data;
  } catch (error) {
    handleRequestError(error, "Failed to upload property images");
  }
};

export const deletePropertyImage = async (propertyId, processedPath) => {
  try {
    const response = await api.delete(`/property/${propertyId}/images`, {
      data: { processedPath },
    });
    return response.data;
  } catch (error) {
    handleRequestError(error, "Failed to delete property image");
  }
};

/**
 * Update property details
 * @param {string} id - Property ID
 * @param {Object} updatedData - Updated property data
 * @returns {Promise<Object>} Updated property
 */
export const updateProperty = async (id, updatedData) => {
  try {
    const idempotencyKey = createIdempotencyKey("property-update", { id, updatedData });
    const response = await api.put(`/property/update/${id}`, updatedData, {
      headers: {
        "Idempotency-Key": idempotencyKey,
      },
    });
    return response.data;
  } catch (error) {
    handleRequestError(error, "Failed to update property");
  }
};

/**
 * Delete property
 * @param {string} id - Property ID
 * @returns {Promise<Object>} Response data
 */
export const deleteProperty = async (id) => {
  try {
    const idempotencyKey = createIdempotencyKey("property-delete", { id });
    const response = await api.delete(`/property/delete/${id}`, {
      headers: {
        "Idempotency-Key": idempotencyKey,
      },
    });
    return response.data;
  } catch (error) {
    handleRequestError(error, "Failed to delete property");
  }
};

/**
 * Request property deletion (for admins/operators without delete permissions)
 * @param {string} propertyId - Property ID
 * @param {string} reason - Reason for deletion
 * @returns {Promise<Object>} Response data
 */
export const requestPropertyDeletion = async (propertyId, reason) => {
  try {
    const response = await api.post(`/property/request-deletion/${propertyId}`, {
      reason
    });
    return response.data;
  } catch (error) {
    handleRequestError(error, "Failed to request property deletion");
  }
};

/**
 * Direct property deletion (for admins/operators with delete permissions)
 * @param {string} propertyId - Property ID
 * @param {string} reason - Reason for deletion
 * @returns {Promise<Object>} Response data
 */
export const deletePropertyDirect = async (propertyId, reason) => {
  try {
    const idempotencyKey = createIdempotencyKey("property-delete", { propertyId, reason });
    const response = await api.delete(`/property/delete/${propertyId}`, {
      data: { reason },
      headers: {
        "Idempotency-Key": idempotencyKey,
      },
    });
    return response.data;
  } catch (error) {
    handleRequestError(error, "Failed to delete property");
  }
};

/**
 * Approve property deletion with token from email
 * @param {string} token - Approval token from email
 * @returns {Promise<Object>} Response data
 */
export const approvePropertyDeletion = async (token) => {
  try {
    const response = await api.post(`/property/approve-deletion/${token}`, {}, {
      timeout: 30000
    });
    return response.data;
  } catch (error) {
    handleRequestError(error, "Failed to approve property deletion");
  }
};

/**
 * Get finance applications for a property
 * @param {string} propertyId - Property ID
 * @returns {Promise<Array>} List of finance applications for the property
 */
export const getPropertyFinanceApplications = async (propertyId) => {
  try {
    const response = await api.get(`/properties/${propertyId}/finance-applications`);
    return response.data;
  } catch (error) {
    log.error(
      `[properties:getPropertyFinanceApplications] > [Request]: GET /properties/${propertyId}/finance-applications > [Response]: error=${error?.message || "unknown"}`
    );
    // Return empty array instead of throwing to prevent UI breaks
    return [];
  }
};

/**
 * Get offers for a property (property-centric endpoint)
 * Alternative to getPropertyOffers from offers.js
 * @param {string} propertyId - Property ID
 * @returns {Promise<Array>} List of offers for the property
 */
export const getPropertyOffersData = async (propertyId) => {
  try {
    const response = await api.get(`/properties/${propertyId}/offers`);
    return response.data;
  } catch (error) {
    log.error(
      `[properties:getPropertyOffersData] > [Request]: GET /properties/${propertyId}/offers > [Response]: error=${error?.message || "unknown"}`
    );
    return [];
  }
};

/**
 * Get activity history for a property
 * Includes views, inquiries, and other property-related activities
 * @param {string} propertyId - Property ID
 * @returns {Promise<Array>} List of activities for the property
 */
export const getPropertyActivities = async (propertyId) => {
  try {
    const response = await api.get(`/properties/${propertyId}/activities`);
    return response.data;
  } catch (error) {
    log.error(
      `[properties:getPropertyActivities] > [Request]: GET /properties/${propertyId}/activities > [Response]: error=${error?.message || "unknown"}`
    );
    return [];
  }
};

/**
 * Request bulk property deletion (for admins/operators without delete permissions)
 * @param {string[]} propertyIds - Array of Property IDs
 * @param {string} reason - Reason for deletion
 * @returns {Promise<Object>} Response data
 */
export const requestPropertyBulkDeletion = async (propertyIds, reason) => {
  try {
    const response = await api.post(`/property/request-bulk-deletion`, {
      propertyIds,
      reason
    });
    return response.data;
  } catch (error) {
    handleRequestError(error, "Failed to request bulk property deletion");
  }
};

/**
 * Direct bulk property deletion (for admins/operators with delete permissions)
 * @param {string[]} propertyIds - Array of Property IDs
 * @param {string} reason - Reason for deletion
 * @returns {Promise<Object>} Response data
 */
export const deletePropertiesBulk = async (propertyIds, reason) => {
  try {
    const response = await api.post(`/property/delete-bulk`, {
      propertyIds,
      reason
    });
    return response.data;
  } catch (error) {
    handleRequestError(error, "Failed to delete properties");
  }
};
